/**
 * Table des especes et de leur caractere.
 *
 * `AnimalDef` est fige (il vient de shared/types/monde.ts): il ne dit que la
 * vitesse, la peur et la couleur. Tout ce qui donne son CARACTERE a une espece
 * vit donc a cote, dans `PROFILS`: le milieu (terre, air, eau, surface), la
 * reaction a l'approche du joueur (detaler, se mettre en boule, tenir sa place,
 * ignorer), la vie sociale (troupeau, banc, petit groupe), le gout du jeu, le
 * rythme nocturne et la demarche a rendre. La simulation et le rendu lisent la meme
 * table: un cerf ne se deplace pas comme un sanglier parce que son profil le dit,
 * pas parce que sa vitesse differe de trois centimetres par seconde.
 *
 * Le monde est entierement paisible: PERSONNE ne chasse personne. Ni le joueur,
 * ni les autres animaux. A la place, les betes se RENCONTRENT: elles se
 * rejoignent, se reniflent, se tournent autour, se poursuivent en cercle sans
 * jamais se rattraper, puis repartent chacune de son cote. Deux especes
 * differentes se saluent au lieu de se fuir, et un petit animal s'excite au
 * passage d'un grand sans la moindre crainte. Aucun animal ne meurt, jamais.
 *
 * Convention de couleur, importante pour le rendu: `couleurs[0]` est la teinte
 * de REFERENCE de l'espece, c'est-a-dire sa tonalite la plus CLAIRE. Le rendu
 * multiplie la couleur d'instance par les couleurs de sommets, donc un motif
 * (ventre clair, masque, taches, rayures) ne peut qu'assombrir la reference. Les
 * autres entrees de `couleurs` sont des variantes individuelles, en general un
 * peu plus sombres: elles assombrissent l'animal entier, motif compris, ce qui
 * est exactement l'allure d'une variation naturelle de pelage.
 */
import type { ItemId } from '../types/items.ts'
import type { AnimalDef, AnimalKind } from '../types/monde.ts'

// ------------------------------------------------------------------- caractere

/** Ou vit l'espece. `surface` flotte sur l'eau, `eau` reste dessous. */
export type Milieu = 'terre' | 'air' | 'eau' | 'surface'

/** Ce que l'animal fait quand le bonhomme s'approche de trop pres. */
export type Reaction =
  /** Il detale. */
  | 'fuite'
  /** Il se roule en boule ou rentre dans sa carapace et attend. */
  | 'boule'
  /** Il ne cede pas un pouce: il fait face et gratte le sol. */
  | 'garde'
  /** Il s'ecarte a peine: le joueur ne l'impressionne pas. */
  | 'indifferent'

/** Vie sociale. Le rayon exact est dans `cohesion`. */
export type Social =
  | 'aucun'
  /** Petit groupe lache: on se perd de vue sans drame. */
  | 'groupe'
  /** Troupeau serre: on se suit, on garde ses distances, on fuit ensemble. */
  | 'troupeau'
  /** Banc de poissons: tres serre, tres reactif. */
  | 'banc'

/** Facon de se deplacer. Le rendu en fait une demarche. */
export type Demarche =
  /** Bonds nets, l'animal quitte le sol (lapin, lievre, ecureuil). */
  | 'sautille'
  /** Trot souple et rebondi (cerf, renne, chevre). */
  | 'trotte'
  /** Pas pose de quadrupede (renard, loup, chameau, sanglier). */
  | 'marche'
  /** Masse qui roule d'une epaule sur l'autre (ours). */
  | 'lourd'
  /** Foulee basse et feline, dos souple (panthere). */
  | 'feline'
  /** Ventre au sol, pattes ecartees (lezard). */
  | 'rampe'
  /** Ondulation laterale du corps entier (serpent). */
  | 'ondule'
  /** Salves breves separees de longs arrets (scorpion). */
  | 'saccade'
  /** Deplacement de cote (crabe). */
  | 'lateral'
  /** Roulis marque a chaque pas (manchot, canard a terre). */
  | 'dandine'
  /** A-coups tres lents (tortue). */
  | 'traine'
  /** Battements d'ailes rapides (oiseau, hibou, mouette). */
  | 'vol_bat'
  /** Ailes fixes, longs cercles (aigle). */
  | 'vol_plane'
  /** Trajet en zigzag et flottement (papillon, luciole). */
  | 'vol_erratique'
  /** Nage souple sous la surface (poisson, phoque). */
  | 'nage'
  /** Nage avec sortie d'eau en arc (dauphin). */
  | 'bond_eau'
  /** Flotte, se laisse porter, plonge la tete (canard). */
  | 'flotte'

/**
 * Vitesse de rotation par defaut, en radians par seconde, deduite de la
 * demarche.
 *
 * Une masse qui roule d'une epaule sur l'autre ne pivote pas comme un papillon:
 * c'est la demarche, pas la vitesse, qui dit a quel point une espece est agile.
 * A 1 rad/s un ours met trois secondes a faire demi-tour, a 4 rad/s un papillon
 * y met huit dixiemes. Une espece peut toujours surcharger sa valeur.
 */
const VIRAGE_DEMARCHE: Record<Demarche, number> = {
  sautille: 3,
  trotte: 2,
  marche: 2.2,
  lourd: 1,
  feline: 2.4,
  rampe: 3,
  ondule: 1.4,
  saccade: 2.6,
  lateral: 2.6,
  dandine: 1.6,
  traine: 0.8,
  vol_bat: 2.6,
  // Le planeur ne vire pas: il incline et decrit un grand cercle.
  vol_plane: 0.7,
  vol_erratique: 4,
  nage: 2.4,
  bond_eau: 2,
  flotte: 1.5,
}

export interface ProfilAnimal {
  milieu: Milieu
  reaction: Reaction
  social: Social
  /** Rayon de cohesion sociale en metres. 0 si l'espece vit seule. */
  cohesion: number
  /**
   * Rayon, en metres, dans lequel l'animal va a la RENCONTRE d'un autre animal,
   * de son espece ou non, pour jouer avec lui. 0 = espece qui ne joue pas.
   */
  curiosite: number
  /**
   * Entrain pendant le jeu, de 0 a 1. Sert au rendu: un lapin bondit et se
   * cabre, une tortue se contente de tendre le cou.
   */
  entrain: number
  /** Vrai si l'espece vit la NUIT et dort le JOUR (hibou, luciole). */
  nocturne: boolean
  demarche: Demarche
  /** Bornes de la cible d'errance, en metres: un sanglier tourne en rond. */
  errance: readonly [number, number]
  /** Vrai si l'espece volante accepte de survoler la mer. */
  survoleEau: boolean
  /** Vrai si l'espece volante se pose pour picorer. */
  sePose: boolean
  /**
   * Vitesse de rotation maximale, en radians par seconde. La simulation fait
   * pivoter l'animal VERS son cap sans jamais depasser cette vitesse: c'est ce
   * qui distingue un animal d'une girouette.
   */
  virage: number
}

/** Profil par defaut: un herbivore solitaire et craintif. */
function profil(p: Partial<ProfilAnimal>): ProfilAnimal {
  const demarche = p.demarche ?? 'marche'
  return {
    milieu: 'terre',
    reaction: 'fuite',
    social: 'aucun',
    cohesion: 0,
    curiosite: 7,
    entrain: 0.5,
    nocturne: false,
    demarche,
    errance: [4, 12],
    survoleEau: false,
    sePose: false,
    virage: VIRAGE_DEMARCHE[demarche],
    ...p,
  }
}

/** Friandises communes, pour ne pas les repeter trente fois. */
const HERBE: ItemId[] = ['feuille', 'fibre', 'fruit']
const BAIES: ItemId[] = ['baie', 'fruit', 'feuille']
const CARNE: ItemId[] = ['poisson', 'brochette']

// ------------------------------------------------------------------- especes

export const ANIMAUX: Record<AnimalKind, AnimalDef> = {
  // ----------------------------------------------------------------- foret
  lapin: {
    kind: 'lapin',
    nom: 'Lapin',
    biomes: ['foret', 'montagne', 'ferme'],
    vitesse: 1.6,
    vitesseFuite: 4.6,
    distanceFuite: 7,
    friandises: ['baie', 'feuille', 'fruit'],
    couleurs: [0xf6f1e8, 0xdccbb4, 0xa89078, 0x77655a],
    taille: 1,
    dortLaNuit: true,
  },
  oiseau: {
    kind: 'oiseau',
    nom: 'Oiseau',
    biomes: ['foret', 'plage', 'montagne', 'jungle', 'ferme'],
    vitesse: 3.4,
    vitesseFuite: 7.5,
    distanceFuite: 6,
    friandises: ['baie', 'figue', 'fruit'],
    couleurs: [0xfbe9c8, 0xf6c9b0, 0xd8e6f6, 0xf3e08a],
    taille: 1.15,
    dortLaNuit: true,
  },
  papillon: {
    kind: 'papillon',
    nom: 'Papillon',
    biomes: ['foret', 'plage', 'desert', 'jungle'],
    vitesse: 2.4,
    vitesseFuite: 3.4,
    distanceFuite: 3,
    friandises: ['fruit', 'baie'],
    couleurs: [0xfff0f8, 0xfff8d8, 0xe8f2ff, 0xffe6cc],
    taille: 1.35,
    dortLaNuit: true,
  },
  singe: {
    kind: 'singe',
    nom: 'Singe',
    biomes: ['foret', 'jungle'],
    vitesse: 2.2,
    vitesseFuite: 5.6,
    distanceFuite: 8,
    friandises: ['noix_coco', 'fruit', 'figue'],
    couleurs: [0xd7a878, 0xbb8d5c, 0x9b7048],
    taille: 1,
    dortLaNuit: true,
  },
  panthere: {
    kind: 'panthere',
    nom: 'Panthère',
    feminin: true,
    biomes: ['foret', 'jungle'],
    vitesse: 1.4,
    vitesseFuite: 6.5,
    // Une panthere ne detale pas devant un bonhomme: elle s'ecarte a peine.
    distanceFuite: 3.5,
    friandises: CARNE,
    couleurs: [0xb9b2ae, 0xa79c9c, 0xc9c0b4],
    taille: 1,
    dortLaNuit: false,
  },
  renard: {
    kind: 'renard',
    nom: 'Renard',
    biomes: ['foret', 'montagne', 'ferme'],
    vitesse: 1.9,
    vitesseFuite: 5.8,
    distanceFuite: 6,
    friandises: ['baie', 'poisson', 'fruit'],
    couleurs: [0xfff2e2, 0xf7e2c6, 0xf0d3ae],
    taille: 1,
    dortLaNuit: false,
  },
  cerf: {
    kind: 'cerf',
    nom: 'Cerf',
    biomes: ['foret', 'ferme'],
    vitesse: 1.5,
    vitesseFuite: 6.2,
    distanceFuite: 11,
    friandises: HERBE,
    couleurs: [0xe4c49a, 0xd0aa7e, 0xb8905f],
    taille: 1,
    dortLaNuit: true,
  },
  sanglier: {
    kind: 'sanglier',
    nom: 'Sanglier',
    biomes: ['foret'],
    vitesse: 1.1,
    vitesseFuite: 4.4,
    // Il tient sa place: il ne se sent menace que de tres pres.
    distanceFuite: 4,
    friandises: ['champignon', 'fruit', 'noix_coco'],
    couleurs: [0x9c9088, 0x86786e, 0x6f635c],
    taille: 1,
    dortLaNuit: false,
  },
  ecureuil: {
    kind: 'ecureuil',
    nom: 'Écureuil',
    biomes: ['foret', 'cristal', 'ferme'],
    vitesse: 2.6,
    vitesseFuite: 5.4,
    distanceFuite: 6,
    friandises: ['noix_coco', 'baie', 'figue'],
    couleurs: [0xffe7cf, 0xf0c9a2, 0xd8a37a, 0xb0b6bc],
    taille: 1.1,
    dortLaNuit: true,
  },
  herisson: {
    kind: 'herisson',
    nom: 'Hérisson',
    biomes: ['foret', 'ferme'],
    vitesse: 0.7,
    vitesseFuite: 1.1,
    distanceFuite: 3.2,
    friandises: ['champignon', 'baie', 'fruit'],
    couleurs: [0xe6d8c2, 0xd2c0a6, 0xbaa88e],
    taille: 1.15,
    dortLaNuit: true,
  },
  luciole: {
    kind: 'luciole',
    nom: 'Luciole',
    feminin: true,
    biomes: ['foret'],
    vitesse: 1.5,
    vitesseFuite: 2.2,
    distanceFuite: 1.6,
    friandises: ['baie', 'fruit'],
    couleurs: [0xdcff8a, 0xbfff70, 0xfff2a0],
    taille: 1.3,
    dortLaNuit: false,
  },

  // -------------------------------------------------------------- montagne
  chevre: {
    kind: 'chevre',
    nom: 'Chèvre de montagne',
    feminin: true,
    biomes: ['montagne', 'glacier', 'ferme', 'cristal'],
    vitesse: 1.3,
    vitesseFuite: 3.8,
    distanceFuite: 8,
    friandises: HERBE,
    couleurs: [0xf6f2ea, 0xdfd4c2, 0xa89a86],
    taille: 1,
    dortLaNuit: true,
  },
  loup: {
    kind: 'loup',
    nom: 'Loup',
    biomes: ['montagne'],
    vitesse: 1.8,
    vitesseFuite: 6,
    distanceFuite: 7,
    friandises: CARNE,
    couleurs: [0xdad6cf, 0xc0bcb6, 0xa3a09b, 0x8c8d92],
    taille: 1,
    dortLaNuit: false,
  },
  aigle: {
    kind: 'aigle',
    nom: 'Aigle',
    biomes: ['montagne'],
    vitesse: 5.5,
    vitesseFuite: 8,
    distanceFuite: 4,
    friandises: ['poisson', 'brochette'],
    couleurs: [0xf1e3c8, 0xe2cfae, 0xd4bd97],
    taille: 1,
    dortLaNuit: false,
  },
  hibou: {
    kind: 'hibou',
    nom: 'Hibou',
    biomes: ['montagne', 'foret'],
    vitesse: 2.6,
    vitesseFuite: 5.5,
    distanceFuite: 5,
    friandises: ['poisson', 'champignon'],
    couleurs: [0xf3e6cd, 0xe0cfb0, 0xc9b795],
    taille: 1,
    dortLaNuit: false,
  },

  // ----------------------------------------------------------- plage et mer
  crabe: {
    kind: 'crabe',
    nom: 'Crabe',
    biomes: ['plage'],
    vitesse: 0.9,
    vitesseFuite: 2.4,
    distanceFuite: 4,
    friandises: ['poisson', 'champignon'],
    couleurs: [0xffb894, 0xf59a72, 0xe4784f],
    taille: 1.1,
    dortLaNuit: false,
  },
  lezard: {
    kind: 'lezard',
    nom: 'Lézard',
    biomes: ['desert', 'plage', 'cristal'],
    vitesse: 1.9,
    vitesseFuite: 4.8,
    distanceFuite: 5,
    friandises: ['figue', 'champignon'],
    couleurs: [0xd8dca0, 0xc6c184, 0xa8b878],
    taille: 1.3,
    dortLaNuit: false,
  },
  mouette: {
    kind: 'mouette',
    nom: 'Mouette',
    feminin: true,
    biomes: ['plage'],
    vitesse: 4.2,
    vitesseFuite: 7,
    distanceFuite: 5,
    friandises: ['poisson', 'beigne', 'brochette'],
    couleurs: [0xffffff, 0xf4f6f8, 0xe8ecf0],
    taille: 1,
    dortLaNuit: true,
  },
  tortue: {
    kind: 'tortue',
    nom: 'Tortue',
    feminin: true,
    biomes: ['plage'],
    vitesse: 0.32,
    vitesseFuite: 0.5,
    distanceFuite: 3,
    friandises: ['feuille', 'fruit', 'baie'],
    couleurs: [0xcfd4a8, 0xb9be92, 0xa2a87e],
    taille: 1.05,
    dortLaNuit: true,
  },
  dauphin: {
    kind: 'dauphin',
    nom: 'Dauphin',
    biomes: ['plage'],
    vitesse: 2.8,
    vitesseFuite: 5.5,
    distanceFuite: 4,
    friandises: ['poisson'],
    couleurs: [0xdfe9f2, 0xcbd9e6, 0xb7c8d8],
    taille: 1,
    dortLaNuit: false,
  },
  poisson_mer: {
    kind: 'poisson_mer',
    nom: 'Poisson',
    biomes: ['plage'],
    vitesse: 1.4,
    vitesseFuite: 3.2,
    distanceFuite: 3,
    friandises: ['baie', 'champignon'],
    couleurs: [0xdff0f4, 0xf4e6b8, 0xf6cfa8],
    taille: 1.3,
    dortLaNuit: false,
  },
  canard: {
    kind: 'canard',
    nom: 'Canard',
    biomes: ['plage', 'foret', 'ferme'],
    vitesse: 0.75,
    vitesseFuite: 1.9,
    distanceFuite: 4.5,
    friandises: ['baie', 'feuille', 'beigne'],
    couleurs: [0xf6efe0, 0xe8dcc6, 0xd6c8ae],
    taille: 1,
    dortLaNuit: true,
  },

  // -------------------------------------------------------------- glacier
  manchot: {
    kind: 'manchot',
    nom: 'Manchot',
    biomes: ['glacier'],
    vitesse: 0.85,
    vitesseFuite: 2.4,
    distanceFuite: 5,
    friandises: ['poisson'],
    couleurs: [0xfbfcfe, 0xeef2f6, 0xe2e8ee],
    taille: 1,
    dortLaNuit: true,
  },
  ours: {
    kind: 'ours',
    nom: 'Ours polaire',
    biomes: ['glacier'],
    vitesse: 1,
    vitesseFuite: 5.5,
    distanceFuite: 4,
    friandises: CARNE,
    couleurs: [0xf7f9fb, 0xeef2f6, 0xe6ebf0],
    taille: 1,
    dortLaNuit: false,
  },
  renne: {
    kind: 'renne',
    nom: 'Renne',
    biomes: ['glacier'],
    vitesse: 1.4,
    vitesseFuite: 5.4,
    distanceFuite: 10,
    friandises: HERBE,
    couleurs: [0xe8ddc8, 0xd4c6ac, 0xbaa88c],
    taille: 1,
    dortLaNuit: true,
  },
  phoque: {
    kind: 'phoque',
    nom: 'Phoque',
    biomes: ['plage', 'glacier'],
    vitesse: 1.5,
    vitesseFuite: 3.4,
    distanceFuite: 4,
    friandises: ['poisson'],
    couleurs: [0xd8dee4, 0xc2cad2, 0xa9b3bd],
    taille: 1,
    dortLaNuit: false,
  },
  lievre_arctique: {
    kind: 'lievre_arctique',
    nom: 'Lièvre arctique',
    biomes: ['glacier'],
    vitesse: 1.9,
    vitesseFuite: 5.6,
    distanceFuite: 8,
    friandises: BAIES,
    couleurs: [0xfdfeff, 0xf1f5f9, 0xe4ebf2],
    taille: 1,
    dortLaNuit: true,
  },

  // --------------------------------------------------------------- desert
  serpent: {
    kind: 'serpent',
    nom: 'Serpent',
    biomes: ['desert', 'jungle'],
    vitesse: 1.1,
    vitesseFuite: 2.8,
    distanceFuite: 4.5,
    friandises: ['poisson', 'figue'],
    couleurs: [0xe8d69a, 0xd4bf80, 0xc0a868],
    taille: 1.2,
    dortLaNuit: false,
  },
  scorpion: {
    kind: 'scorpion',
    nom: 'Scorpion',
    biomes: ['desert'],
    vitesse: 1.5,
    vitesseFuite: 3.2,
    distanceFuite: 3.4,
    friandises: ['figue', 'champignon'],
    couleurs: [0xdcbf8e, 0xc7a677, 0xb08e62],
    taille: 1.45,
    dortLaNuit: false,
  },
  chameau: {
    kind: 'chameau',
    nom: 'Chameau',
    biomes: ['desert'],
    vitesse: 0.95,
    vitesseFuite: 3.2,
    // Un chameau ne s'affole pas: il s'ecarte de deux pas.
    distanceFuite: 3.5,
    friandises: HERBE,
    couleurs: [0xe6cca0, 0xd4b788, 0xc0a273],
    taille: 1,
    dortLaNuit: false,
  },
}

export function animalDef(k: AnimalKind): AnimalDef {
  return ANIMAUX[k]
}

// ------------------------------------------------------------------- profils

export const PROFILS: Record<AnimalKind, ProfilAnimal> = {
  // Le lapin detale par bonds, et se poursuit en rond avec un congenere.
  lapin: profil({ demarche: 'sautille', errance: [3, 9], curiosite: 9, entrain: 0.95 }),
  // L'oiseau vole en zigzag puis se pose pour picorer.
  oiseau: profil({
    milieu: 'air',
    demarche: 'vol_bat',
    sePose: true,
    social: 'groupe',
    cohesion: 12,
    errance: [8, 20],
    curiosite: 10,
    entrain: 0.7,
  }),
  // Le papillon flotte de fleur en fleur et tourne en spirale avec un autre.
  papillon: profil({ milieu: 'air', demarche: 'vol_erratique', sePose: true, errance: [2, 6], curiosite: 5, entrain: 0.85 }),
  // Le singe vit en bande et bondit haut: le plus joueur du bestiaire.
  singe: profil({ demarche: 'sautille', social: 'groupe', cohesion: 14, errance: [4, 12], curiosite: 13, entrain: 1 }),
  // La panthere a une foulee basse et feline; rien ne l'emeut, elle joue peu.
  panthere: profil({
    reaction: 'indifferent',
    demarche: 'feline',
    errance: [8, 22],
    curiosite: 6,
    entrain: 0.35,
  }),
  // Le renard va renifler tout ce qui passe, museau au sol, et fait des bonds.
  renard: profil({ demarche: 'marche', errance: [6, 18], curiosite: 11, entrain: 0.85 }),
  // Le cerf vit en troupeau: il suit les autres et baisse la tete vers les petits.
  cerf: profil({ demarche: 'trotte', social: 'troupeau', cohesion: 16, errance: [6, 16], curiosite: 10, entrain: 0.5 }),
  // Le sanglier tient sa place: il fait face et gratte le sol, sans menace.
  sanglier: profil({ reaction: 'garde', demarche: 'marche', errance: [2, 6], curiosite: 5, entrain: 0.3 }),
  // L'ecureuil part en fleche, s'arrete net, et fait le tour de qui passe.
  ecureuil: profil({ demarche: 'sautille', errance: [2, 7], curiosite: 8, entrain: 0.95 }),
  // Le herisson ne detale pas: il se met en boule et attend que ca passe.
  herisson: profil({ reaction: 'boule', demarche: 'traine', errance: [2, 6], curiosite: 3, entrain: 0.15 }),
  // La luciole ne vit que la nuit: le jour, elle est eteinte dans l'herbe.
  luciole: profil({
    milieu: 'air',
    demarche: 'vol_erratique',
    nocturne: true,
    social: 'groupe',
    cohesion: 9,
    errance: [2, 7],
    curiosite: 6,
    entrain: 0.6,
  }),

  // La chevre grimpe en petit troupeau et se cabre pour jouer des cornes.
  chevre: profil({ demarche: 'trotte', social: 'troupeau', cohesion: 12, errance: [4, 12], curiosite: 9, entrain: 0.7 }),
  // Le loup vit en meute: il tourne autour des siens, joue, et ne chasse rien.
  loup: profil({ demarche: 'marche', social: 'troupeau', cohesion: 18, errance: [10, 26], curiosite: 12, entrain: 0.7 }),
  // L'aigle plane en grands cercles, tres haut, sans battre des ailes ni jouer.
  aigle: profil({
    milieu: 'air',
    demarche: 'vol_plane',
    survoleEau: true,
    reaction: 'indifferent',
    errance: [20, 45],
    curiosite: 0,
    entrain: 0,
  }),
  // Le hibou est nocturne: perche le jour, en vol silencieux la nuit.
  hibou: profil({ milieu: 'air', demarche: 'vol_bat', nocturne: true, sePose: true, errance: [8, 22], curiosite: 6, entrain: 0.3 }),

  // Le crabe se deplace de cote et se tourne autour, pinces levees.
  crabe: profil({ demarche: 'lateral', errance: [2, 7], curiosite: 4, entrain: 0.45 }),
  // Le lezard file d'un caillou a l'autre puis se fige au soleil.
  lezard: profil({ demarche: 'rampe', errance: [2, 8], curiosite: 3, entrain: 0.25 }),
  // La mouette tournoie au-dessus de la mer, en bande, puis se pose sur le sable.
  mouette: profil({
    milieu: 'air',
    demarche: 'vol_bat',
    survoleEau: true,
    sePose: true,
    social: 'groupe',
    cohesion: 16,
    errance: [10, 28],
    curiosite: 14,
    entrain: 0.7,
  }),
  // La tortue est lente et rentre dans sa carapace.
  tortue: profil({ reaction: 'boule', demarche: 'traine', errance: [2, 5], curiosite: 2.5, entrain: 0.1 }),
  // Le dauphin nage en groupe, se tourne autour et saute hors de l'eau.
  dauphin: profil({
    milieu: 'eau',
    demarche: 'bond_eau',
    social: 'groupe',
    cohesion: 14,
    reaction: 'indifferent',
    // De longues traversees: un dauphin file droit, il ne pointille pas la baie.
    errance: [16, 38],
    curiosite: 14,
    entrain: 1,
  }),
  // Le poisson vit en banc tres serre.
  poisson_mer: profil({ milieu: 'eau', demarche: 'nage', social: 'banc', cohesion: 6, errance: [8, 18], curiosite: 4, entrain: 0.3 }),
  // Le canard flotte a la surface et plonge la tete.
  canard: profil({ milieu: 'surface', demarche: 'flotte', social: 'groupe', cohesion: 8, errance: [2, 8], curiosite: 6, entrain: 0.5 }),

  // Le manchot vit en colonie, se dandine et se pousse gentiment.
  manchot: profil({ demarche: 'dandine', social: 'groupe', cohesion: 10, errance: [2, 8], curiosite: 8, entrain: 0.6 }),
  // L'ours est lourd, rien ne l'inquiete, et il se roule volontiers.
  ours: profil({ reaction: 'indifferent', demarche: 'lourd', errance: [8, 20], curiosite: 7, entrain: 0.5 }),
  // Le renne migre en troupeau serre.
  renne: profil({ demarche: 'trotte', social: 'troupeau', cohesion: 18, errance: [8, 20], curiosite: 8, entrain: 0.45 }),
  // Le phoque nage, remonte souffler et fait des tonneaux.
  phoque: profil({
    milieu: 'eau',
    demarche: 'nage',
    reaction: 'indifferent',
    social: 'groupe',
    cohesion: 10,
    errance: [12, 26],
    curiosite: 10,
    entrain: 0.8,
  }),
  // Le lievre arctique detale en longs bonds et se poursuit dans la neige.
  lievre_arctique: profil({ demarche: 'sautille', errance: [4, 12], curiosite: 9, entrain: 0.85 }),

  // Le serpent ondule au ras du sable, seul.
  serpent: profil({ demarche: 'ondule', errance: [3, 10], curiosite: 3, entrain: 0.1 }),
  // Le scorpion detale par saccades et s'immobilise.
  scorpion: profil({ demarche: 'saccade', errance: [2, 6], curiosite: 2.5, entrain: 0.1 }),
  // Le chameau va au pas, en petite caravane.
  chameau: profil({
    reaction: 'indifferent',
    demarche: 'marche',
    social: 'troupeau',
    cohesion: 14,
    errance: [10, 24],
    curiosite: 7,
    entrain: 0.3,
  }),
}

/** Milieu de chaque espece, extrait des profils pour les lectures rapides. */
export const MILIEU: Record<AnimalKind, Milieu> = (() => {
  const m = {} as Record<AnimalKind, Milieu>
  for (const k of Object.keys(PROFILS) as AnimalKind[]) m[k] = PROFILS[k].milieu
  return m
})()

/** Especes qui volent: elles survolent le sol et ne broutent qu'en se posant. */
export const ESPECES_VOLANTES: readonly AnimalKind[] = (Object.keys(PROFILS) as AnimalKind[]).filter(
  (k) => PROFILS[k].milieu === 'air',
)

/** Especes qui vivent dans l'eau: elles doivent y RESTER. */
export const ESPECES_AQUATIQUES: readonly AnimalKind[] = (Object.keys(PROFILS) as AnimalKind[]).filter(
  (k) => PROFILS[k].milieu === 'eau' || PROFILS[k].milieu === 'surface',
)

/** Toutes les especes, dans l'ordre d'apparition au journal. */
export const TOUTES_ESPECES: readonly AnimalKind[] = [
  'lapin',
  'oiseau',
  'papillon',
  'singe',
  'panthere',
  'renard',
  'cerf',
  'sanglier',
  'ecureuil',
  'herisson',
  'luciole',
  'chevre',
  'loup',
  'aigle',
  'hibou',
  'crabe',
  'lezard',
  'mouette',
  'tortue',
  'dauphin',
  'poisson_mer',
  'canard',
  'manchot',
  'ours',
  'renne',
  'phoque',
  'lievre_arctique',
  'serpent',
  'scorpion',
  'chameau',
]

/** Les especes que le jeu instancie. Tout le bestiaire est en jeu. */
export const ESPECES_PHASE_1: readonly AnimalKind[] = TOUTES_ESPECES

/**
 * Population visee par espece sur tout le monde.
 *
 * Somme des quotas: 344. Le monde en place moins (une partie des tirages tombe
 * dans l'eau, sur une falaise ou dans le mauvais biome), autour de 300. Les
 * grosses especes restent rares: on remarque un ours, on ne le croise pas tous
 * les dix metres. Les petites especes bon marche en triangles (luciole,
 * poisson, papillon) peuvent etre nombreuses sans peser.
 */
export const POPULATION: Record<AnimalKind, number> = {
  // Foret
  lapin: 30,
  oiseau: 26,
  papillon: 26,
  singe: 12,
  panthere: 4,
  renard: 9,
  cerf: 16,
  sanglier: 7,
  ecureuil: 18,
  herisson: 10,
  luciole: 34,
  // Montagne
  chevre: 13,
  loup: 8,
  aigle: 4,
  hibou: 5,
  // Plage et mer
  crabe: 20,
  lezard: 18,
  mouette: 20,
  tortue: 6,
  dauphin: 8,
  poisson_mer: 28,
  canard: 10,
  // Glacier
  manchot: 18,
  ours: 4,
  renne: 14,
  phoque: 5,
  lievre_arctique: 13,
  // Desert
  serpent: 12,
  scorpion: 13,
  chameau: 5,
}

/**
 * COMBIEN DE CHAQUE ESPECE SUR L'ILE 2 — une table a part, et il le faut.
 *
 * Reutiliser `POPULATION` aurait double chaque espece dans le monde entier et
 * mis cinq chameaux sur une ile dont le desert fait trois hectares, sans rien
 * donner a la jungle. Les quotas suivent ce que les onze regions offrent
 * vraiment: beaucoup de foret et de jungle, un petit desert, un glacier au
 * sommet, des plages etroites.
 *
 * Une espece a zero n'est pas un oubli: elle n'a pas sa place la-bas. La
 * panthere, l'ours et le loup restent des betes du monde 1 — on remarque un
 * predateur, on ne le croise pas sur deux iles a la fois.
 */
export const POPULATION_ILE2: Record<AnimalKind, number> = {
  // Le Bosquet des Rires: la jungle est ce que l'ile a de plus dense.
  singe: 14,
  papillon: 22,
  serpent: 6,
  // La Foret Fabula, le Lac Lilas et le Jardin Scintillant.
  lapin: 16,
  oiseau: 22,
  ecureuil: 12,
  cerf: 10,
  herisson: 6,
  luciole: 24,
  renard: 5,
  sanglier: 4,
  // La Ferme de l'Amitie. Elle a longtemps semble vide, et pour une raison
  // qu'aucune relecture n'aurait donnee: sa seule espece dediee etait le
  // CANARD, qui vit sur l'eau — il ne se posait donc jamais dans un champ.
  canard: 12,
  // Le Canyon de Cristal et la Cascade de l'Etoile.
  chevre: 10,
  hibou: 4,
  aigle: 3,
  // Le Paradis Polaire.
  manchot: 12,
  renne: 8,
  lievre_arctique: 9,
  phoque: 4,
  // Les Sables Scintillants.
  lezard: 12,
  scorpion: 8,
  chameau: 3,
  // L'Anse des Nuages et la Plage de la Brise.
  crabe: 14,
  mouette: 14,
  tortue: 4,
  dauphin: 5,
  poisson_mer: 20,
  // Ce qui reste au monde 1.
  panthere: 0,
  loup: 0,
  ours: 0,
}

/**
 * Altitude de vol des especes volantes, en metres au-dessus du sol.
 *
 * C'est l'altitude de CROISIERE. Une espece qui se pose (`sePose`) descend au
 * sol quand elle broute ou qu'elle dort: voir `yAnimal` dans shared/sim.
 */
export const ALTITUDE_VOL: Record<AnimalKind, number> = {
  lapin: 0,
  oiseau: 1.8,
  papillon: 0.9,
  singe: 0,
  panthere: 0,
  renard: 0,
  cerf: 0,
  sanglier: 0,
  ecureuil: 0,
  herisson: 0,
  luciole: 1.1,
  chevre: 0,
  loup: 0,
  aigle: 17,
  hibou: 4.5,
  crabe: 0,
  lezard: 0,
  mouette: 6.5,
  tortue: 0,
  dauphin: 0,
  poisson_mer: 0,
  canard: 0,
  manchot: 0,
  ours: 0,
  renne: 0,
  phoque: 0,
  lievre_arctique: 0,
  serpent: 0,
  scorpion: 0,
  chameau: 0,
}

/**
 * Altitude de repos, en metres au-dessus du sol. Presque toujours zero: un
 * hibou endormi fait exception, il se perche.
 */
export const ALTITUDE_REPOS: Record<AnimalKind, number> = (() => {
  const m = {} as Record<AnimalKind, number>
  for (const k of Object.keys(ANIMAUX) as AnimalKind[]) m[k] = 0
  m.hibou = 2.6
  return m
})()

/**
 * Profondeur d'immersion des especes d'eau, en metres sous le niveau de la mer.
 * Le canard flotte (0), le poisson nage juste dessous, le dauphin entre deux.
 */
export const IMMERSION: Record<AnimalKind, number> = (() => {
  const m = {} as Record<AnimalKind, number>
  for (const k of Object.keys(ANIMAUX) as AnimalKind[]) m[k] = 0
  m.poisson_mer = 0.34
  m.dauphin = 0.1
  m.phoque = 0.18
  m.canard = 0.05
  return m
})()

/** Profondeur d'eau minimale, en metres, pour qu'une espece d'eau tienne. */
export const PROFONDEUR_MIN: Record<AnimalKind, number> = (() => {
  const m = {} as Record<AnimalKind, number>
  for (const k of Object.keys(ANIMAUX) as AnimalKind[]) m[k] = 0
  m.poisson_mer = 0.9
  m.dauphin = 0.95
  m.phoque = 1.05
  m.canard = 0.35
  return m
})()
